import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Firm-wide notification policy editor (T-0112 / C20-122, E09-F04).
 *
 * Wires the firm-admin policy editor to the real backend surface landed
 * in PR #133 (`T-0112 follow-up` Wave 9):
 *
 *   - GET  /api/v1/admin/firm/notification-policies
 *       → { policies: [{ event_kind, in_app, email, digest,
 *                         severity_threshold, audience_scopes[],
 *                         updated_at, updated_by }, ...] }
 *   - PUT  /api/v1/admin/firm/notification-policies
 *       → { updated: N }
 *
 * UI ↔ backend shape mapping
 * --------------------------
 * The UI was scaffolded against a richer policy shape that carries a
 * human-readable `label` + a stable `category` slug (used to group rows
 * in the editor + the email-template modal). The backend only persists
 * the routing fields (channel toggles + severity + audience). To keep
 * the editor + email-template modal + channel-override editor working
 * without a UI rewrite, this module:
 *
 *   1. Maintains a static `EVENT_METADATA` table that maps every event
 *      kind in the notification taxonomy → its display label + category.
 *      Identical to the prototype's `D.notificationEvents` block.
 *   2. Hydrates backend rows into the UI's `FirmNotificationPolicy`
 *      shape on read (server `event_kind` → UI `event_type`, server
 *      `severity_threshold` → UI `severity`, lookup label + category).
 *   3. Strips the synthetic fields on write so the PUT body matches the
 *      backend's strict validation.
 *
 * Backend reads the canonical event-kind list from
 * `packages/notification-fanout/src/taxonomy.ts`; the local
 * `EVENT_METADATA` table here is intentionally a superset of the
 * shipped taxonomy so the editor renders rows for every fan-out event.
 * Rows from the backend whose kind is not in the local metadata fall
 * back to a sensible default (titlecased kind + "Other" category).
 */

// ===========================================================================
// Severity / audience enums — match backend
// ===========================================================================

export const NotificationSeveritySchema = z.enum(['info', 'warning', 'high', 'critical']);
export type NotificationSeverity = z.infer<typeof NotificationSeveritySchema>;

export const NotificationAudienceSchema = z.enum(['firm_admin', 'engagement_lead', 'consultant']);
export type NotificationAudience = z.infer<typeof NotificationAudienceSchema>;

// ===========================================================================
// UI policy shape — keeps the editor + modal contracts stable.
// ===========================================================================

export interface FirmNotificationPolicy {
  /**
   * Canonical event kind from the notification taxonomy. The UI keeps
   * the historical `event_type` field name for back-compat with the
   * editor + email-template modal; the wire format uses `event_kind`.
   */
  event_type: string;
  /** Human-readable label (UI-only, derived from EVENT_METADATA). */
  label: string;
  /** Category slug (UI-only, derived from EVENT_METADATA). */
  category: string;
  /** Effective severity threshold for this event. */
  severity: NotificationSeverity;
  /** Firm-default in-app delivery enabled. */
  in_app: boolean;
  /** Firm-default email delivery enabled. */
  email: boolean;
  /** Firm-default daily-digest delivery enabled. */
  digest: boolean;
  /** Audience scopes that should receive this event. */
  audience_scopes: NotificationAudience[];
}

export interface FirmNotificationPoliciesResult {
  policies: FirmNotificationPolicy[];
}

// ===========================================================================
// Static event metadata — display label + category by event_kind.
// Mirrors the prototype's `D.notificationEvents` block. Used as a
// hydration table for backend rows (which only carry routing fields).
// ===========================================================================

interface EventMetadata {
  label: string;
  category: string;
}

const EVENT_METADATA: Record<string, EventMetadata> = {
  // AI / proposal flow
  'proposals.ready': { label: 'AI proposals ready', category: 'AI' },
  'reeval.flagged': { label: 'Re-evaluation flagged', category: 'AI' },
  'routing.requested': { label: 'Routing requested', category: 'AI' },
  'routing.decided': { label: 'Routing decided', category: 'AI' },
  // Snapshots + publishes
  'snapshot.captured': { label: 'Snapshot captured', category: 'Snapshots' },
  'pulse.published': { label: 'Contract pulse published', category: 'Snapshots' },
  'synthesis.published': { label: 'Health synthesis published', category: 'Snapshots' },
  'readiness.report_published': { label: 'Readiness report published', category: 'Snapshots' },
  // Registers
  'register.changed': { label: 'Register entry changed', category: 'Registers' },
  'override.captured': { label: 'Override captured', category: 'Registers' },
  // Health domain ratings + thresholds
  'domain.rating_changed': { label: 'Domain rating changed', category: 'Health' },
  'finding.severity_crossed': { label: 'Finding severity crossed', category: 'Health' },
  // Readiness
  'score.threshold_crossed': { label: 'Readiness band crossed', category: 'Readiness' },
  // Contract obligations
  'deliverable.overdue': { label: 'Deliverable past due', category: 'Contract' },
  'deliverable.rejected': { label: 'Deliverable rejected', category: 'Contract' },
  'deviation.logged': { label: 'Deviation logged', category: 'Contract' },
  'deviation.escalated': { label: 'Deviation past its response deadline', category: 'Contract' },
  // Documents / ingestion
  'document.parsed': { label: 'Document parsed', category: 'Documents' },
  'document.indexed': { label: 'Document indexed', category: 'Documents' },
  // Schedule (T-0075 backend)
  'milestone.slipped': { label: 'Milestone slipped', category: 'Health' },
  // Requirement library AI intake (T-0157) — only the curator who started the run
  'library_intake.failed': { label: 'Document import failed', category: 'Requirement library' },
  'library_intake.extracted': {
    label: 'Document import ready to review',
    category: 'Requirement library',
  },
  // Engagement / setup
  'engagement.kickoff_started': { label: 'Kickoff started', category: 'Engagement' },
  'engagement.kickoff_completed': { label: 'Kickoff completed', category: 'Engagement' },
};

function metadataFor(eventKind: string): EventMetadata {
  const known = EVENT_METADATA[eventKind];
  if (known) return known;
  // Fallback for unknown kinds — title-case the kind, group as "Other".
  const label = eventKind
    .split('.')
    .pop()!
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return { label, category: 'Other' };
}

// ===========================================================================
// Stable category order for visual grouping (super-set of EVENT_METADATA
// categories). Any new category that lands via metadata fallback ("Other")
// renders after the known ones.
// ===========================================================================

export const FIRM_POLICY_CATEGORY_ORDER: readonly string[] = [
  'AI',
  'Registers',
  'Snapshots',
  'Readiness',
  'Health',
  'Contract',
  'Documents',
  'Engagement',
  'Other',
] as const;

// ===========================================================================
// Wire shape — what the backend returns.
// ===========================================================================

const BackendPolicySchema = z.object({
  event_kind: z.string(),
  in_app: z.boolean(),
  email: z.boolean(),
  digest: z.boolean(),
  severity_threshold: NotificationSeveritySchema,
  audience_scopes: z.array(NotificationAudienceSchema),
  updated_at: z.string(),
  updated_by: z.string().nullable(),
});

const GetFirmNotificationPoliciesResponseSchema = z.object({
  policies: z.array(BackendPolicySchema),
});

type BackendPolicy = z.infer<typeof BackendPolicySchema>;

function hydrate(row: BackendPolicy): FirmNotificationPolicy {
  const meta = metadataFor(row.event_kind);
  return {
    event_type: row.event_kind,
    label: meta.label,
    category: meta.category,
    severity: row.severity_threshold,
    in_app: row.in_app,
    email: row.email,
    digest: row.digest,
    audience_scopes: row.audience_scopes,
  };
}

// ===========================================================================
// GET — list firm-default policies.
// ===========================================================================

export function getFirmNotificationPolicies(): Promise<FirmNotificationPoliciesResult> {
  return apiClient(
    '/api/v1/admin/firm/notification-policies',
    GetFirmNotificationPoliciesResponseSchema,
  ).then((res) => ({
    policies: res.policies.map(hydrate),
  }));
}

export function useFirmNotificationPoliciesQuery() {
  return useQuery({
    queryKey: ['firm-notifications', 'policies'] as const,
    queryFn: getFirmNotificationPolicies,
    staleTime: 30_000,
  });
}

// ===========================================================================
// PUT — bulk upsert.
// ===========================================================================

const PutFirmNotificationPoliciesResponseSchema = z.object({
  updated: z.number().int().nonnegative(),
});
export type PutFirmNotificationPoliciesResult = z.infer<
  typeof PutFirmNotificationPoliciesResponseSchema
>;

/**
 * Strip the UI's synthetic `label` / `category` fields and rename
 * `event_type` → `event_kind` / `severity` → `severity_threshold` so
 * the PUT body matches backend validation.
 */
function toWire(p: FirmNotificationPolicy): {
  event_kind: string;
  in_app: boolean;
  email: boolean;
  digest: boolean;
  severity_threshold: NotificationSeverity;
  audience_scopes: NotificationAudience[];
} {
  return {
    event_kind: p.event_type,
    in_app: p.in_app,
    email: p.email,
    digest: p.digest,
    severity_threshold: p.severity,
    audience_scopes: p.audience_scopes,
  };
}

export function putFirmNotificationPolicies(
  policies: FirmNotificationPolicy[],
): Promise<PutFirmNotificationPoliciesResult> {
  return apiClient(
    '/api/v1/admin/firm/notification-policies',
    PutFirmNotificationPoliciesResponseSchema,
    { method: 'PUT', body: { policies: policies.map(toWire) } },
  );
}

export function usePutFirmNotificationPoliciesMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: putFirmNotificationPolicies,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['firm-notifications'] });
    },
  });
}
